import * as React from 'react'
import { Link } from 'react-router-dom'
import { Copy, Pause, Play, Plus, Sparkles, Trash2 } from 'lucide-react'
import {
  useCreateRule, useDeleteRule, useRuleMatches, useRuleRuns, useRules, useRuleVersions,
  useUpdateRule,
} from '@/lib/queries'
import { ApiError } from '@/lib/api'
import type { Rule } from '@/lib/types'
import { PageHeader } from '@/components/layout/AppShell'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tooltip } from '@/components/ui/tooltip'
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states'
import { MatchStatusBadge, StatusBadge } from '@/components/ui/status'
import { useToast } from '@/components/ui/toast'
import { useAuth } from '@/lib/auth'
import { formatDateTime, formatNumber } from '@/lib/utils'

export default function RulesPage() {
  const { data, isLoading, error, refetch } = useRules()
  const [selected, setSelected] = React.useState<Rule | null>(null)
  const [confirmDelete, setConfirmDelete] = React.useState<Rule | null>(null)
  const [purgeDomains, setPurgeDomains] = React.useState(false)
  const update = useUpdateRule()
  const remove = useDeleteRule()
  const create = useCreateRule()
  const toast = useToast()
  const { user } = useAuth()

  async function toggleStatus(rule: Rule) {
    const next = rule.status === 'active' ? 'paused' : 'active'
    try {
      await update.mutateAsync({ id: rule.id, version: rule.version, body: { status: next } })
      toast.success(next === 'active' ? '规则已启用' : '规则已停用')
    } catch (err) {
      toast.error('操作失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  async function clone(rule: Rule) {
    try {
      const res = await create.mutateAsync({
        rule_json: { ...rule.rule_json, name: `${rule.name} 副本` },
        name: `${rule.name} 副本`,
        description: rule.description,
        status: 'draft',
      })
      toast.success('已克隆为草稿', res.rule.name)
    } catch (err) {
      toast.error('克隆失败', err instanceof ApiError ? err.message : undefined)
    }
  }

  return (
    <>
      <PageHeader
        title="规则管理"
        description="同一条规则既可作为即时查询过滤器，也可作为持续扫描任务。"
        actions={
          <Button size="sm" asChild>
            <Link to="/rules/new"><Plus className="h-3.5 w-3.5" aria-hidden /> 新建规则</Link>
          </Button>
        }
      />

      {error ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : isLoading ? (
        <LoadingState rows={4} />
      ) : (data?.items.length ?? 0) === 0 ? (
        <EmptyState
          icon={Sparkles}
          title="还没有规则"
          description="创建第一条规则，开始持续发现符合条件的域名。"
          action={<Button asChild><Link to="/rules/new">新建规则</Link></Button>}
        />
      ) : (
        <div className="grid gap-3">
          {data!.items.map((rule) => (
            <Card key={rule.id}>
              <CardContent className="flex flex-wrap items-center gap-4 p-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/rules/${rule.id}`}
                      className="truncate font-medium text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {rule.name}
                    </Link>
                    <Badge variant={statusVariant(rule.status)}>{statusLabel(rule.status)}</Badge>
                    <Badge variant="outline">{rule.mode === 'continuous' ? '持续扫描' : '即时查询'}</Badge>
                    <Badge variant="muted">v{rule.version}</Badge>
                  </div>
                  {rule.description && (
                    <p className="mt-1 truncate text-xs text-muted-foreground">{rule.description}</p>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">
                    命中 {formatNumber(rule.match_count)} · 最后运行 {formatDateTime(rule.last_run_at, user?.timezone)}
                    {rule.next_run_at && ` · 下次 ${formatDateTime(rule.next_run_at, user?.timezone)}`}
                  </p>
                  {rule.last_error && (
                    <p role="alert" className="mt-1 truncate text-xs text-destructive">
                      错误：{rule.last_error}
                    </p>
                  )}
                </div>

                <div className="flex shrink-0 flex-wrap items-center gap-1">
                  <Button variant="outline" size="sm" onClick={() => setSelected(rule)}>查看命中</Button>
                  <Tooltip content={rule.status === 'active' ? '停用' : '启用'}>
                    <Button variant="ghost" size="icon-sm" onClick={() => void toggleStatus(rule)} aria-label={rule.status === 'active' ? '停用规则' : '启用规则'}>
                      {rule.status === 'active' ? <Pause className="h-3.5 w-3.5" aria-hidden /> : <Play className="h-3.5 w-3.5" aria-hidden />}
                    </Button>
                  </Tooltip>
                  <Tooltip content="克隆为草稿">
                    <Button variant="ghost" size="icon-sm" onClick={() => void clone(rule)} aria-label="克隆规则">
                      <Copy className="h-3.5 w-3.5" aria-hidden />
                    </Button>
                  </Tooltip>
                  <Tooltip content="删除（软删除，保留历史命中与审计）">
                    <Button variant="ghost" size="icon-sm" onClick={() => setConfirmDelete(rule)} aria-label="删除规则">
                      <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden />
                    </Button>
                  </Tooltip>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <RuleDetailDialog rule={selected} onClose={() => setSelected(null)} />

      {/* §5.8: destructive actions get a second confirmation that states the
          consequence and what survives. */}
      <Dialog open={Boolean(confirmDelete)} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>删除规则「{confirmDelete?.name}」？</DialogTitle>
            <DialogDescription>
              规则会被软删除并停止运行。历史命中记录与审计日志会保留。
            </DialogDescription>
          </DialogHeader>

          {/* Opt-in, and separate from deleting the rule: the rule is yours,
              the domains are shared, and only the ones that exist solely
              because of this rule can go. */}
          <label className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs">
            <Checkbox
              checked={purgeDomains}
              onCheckedChange={(v) => setPurgeDomains(Boolean(v))}
              aria-label="同时删除该规则产生的域名"
            />
            <span>
              <span className="font-medium text-destructive">同时删除该规则产生的域名</span>
              <span className="mt-1 block text-muted-foreground">
                仅删除「只因这条规则而存在」的域名：来源只有规则生成、没有被其他规则命中、
                没有被监控、没有被收藏。其余会保留并在结果中说明。此操作不可撤销。
              </span>
            </span>
          </label>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>取消</Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={async () => {
                if (!confirmDelete) return
                try {
                  const res = await remove.mutateAsync({
                    id: confirmDelete.id,
                    purgeDomains,
                  })
                  toast.success('规则已删除', res.notice)
                } catch (err) {
                  toast.error('删除失败', err instanceof ApiError ? err.message : undefined)
                } finally {
                  setConfirmDelete(null)
                  setPurgeDomains(false)
                }
              }}
            >
              {remove.isPending ? '删除中…' : '确认删除'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function RuleDetailDialog({ rule, onClose }: { rule: Rule | null; onClose: () => void }) {
  const [matchStatus, setMatchStatus] = React.useState('matched')
  const matches = useRuleMatches(rule?.id ?? null, matchStatus)
  const runs = useRuleRuns(rule?.id ?? null, true)
  const versions = useRuleVersions(rule?.id ?? null)
  const { user } = useAuth()

  return (
    <Dialog open={Boolean(rule)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{rule?.name}</DialogTitle>
          <DialogDescription>
            默认只展示已确认命中；「待确认」结果尚未经外部确认，请勿据此认为可注册。
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="matches">
          <TabsList>
            <TabsTrigger value="matches">命中结果</TabsTrigger>
            <TabsTrigger value="runs">运行历史</TabsTrigger>
            <TabsTrigger value="versions">版本历史</TabsTrigger>
          </TabsList>

          <TabsContent value="matches">
            <div className="mb-3 flex flex-wrap gap-2">
              {[
                { value: 'matched', label: '已确认命中' },
                { value: 'pending_verification', label: '待确认' },
                { value: 'no_longer_matched', label: '确认后不符合' },
                { value: 'stale', label: '确认超时' },
                { value: 'any', label: '全部' },
              ].map((o) => (
                <button
                  key={o.value}
                  onClick={() => setMatchStatus(o.value)}
                  aria-pressed={matchStatus === o.value}
                  className={`rounded-md border px-2.5 py-1 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    matchStatus === o.value ? 'border-primary bg-primary/10 font-medium text-primary' : 'hover:bg-accent'
                  }`}
                >
                  {o.label}
                  {matches.data?.counts?.[o.value] != null && (
                    <span className="ml-1 text-muted-foreground">
                      {formatNumber(matches.data.counts[o.value])}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {matches.isLoading ? (
              <LoadingState rows={5} />
            ) : (matches.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="暂无该状态的命中结果" />
            ) : (
              <div className="scroll-x max-h-96 overflow-y-auto rounded-md border">
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="sticky top-0 border-b bg-muted/60">
                    <tr>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">域名</th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">命中状态</th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">当前状态</th>
                      <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">最近命中</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matches.data!.items.map((m) => (
                      <tr key={m.id} className="border-b last:border-0">
                        <td className="px-3 py-2 font-mono text-xs">{m.domain}</td>
                        <td className="px-3 py-2">
                          <MatchStatusBadge status={m.match_status} />
                          {m.match_status === 'pending_verification' && m.verify_attempts > 0 && (
                            <span className="ml-1 text-[11px] text-muted-foreground">
                              已尝试 {m.verify_attempts} 次
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <StatusBadge
                            status={m.current_status}
                            source={m.status_source}
                            confirmedAt={m.last_resolved_at}
                          />
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          {formatDateTime(m.last_matched_at, user?.timezone)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="runs">
            {runs.isLoading ? (
              <LoadingState rows={4} />
            ) : (runs.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="尚未运行过" />
            ) : (
              <div className="scroll-x rounded-md border">
                {/* A table, not cards: consecutive runs of one rule are nearly
                    identical, and the only thing worth seeing is which number
                    moved. Stacked cards put each figure at a different x
                    position, so a change can only be found by reading every
                    line. Aligned columns of tabular figures make it a glance. */}
                <table className="w-full min-w-[640px] text-xs">
                  <caption className="sr-only">运行历史</caption>
                  <thead className="border-b bg-muted/40 text-muted-foreground">
                    <tr>
                      <th scope="col" className="px-3 py-2 text-left font-medium">时间</th>
                      <th scope="col" className="px-3 py-2 text-left font-medium">状态</th>
                      <th scope="col" className="px-3 py-2 text-left font-medium">触发</th>
                      <th scope="col" className="px-3 py-2 text-left font-medium">版本</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">候选</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">命中</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">待确认</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">拒绝</th>
                      <th scope="col" className="px-3 py-2 text-right font-medium">新建域名</th>
                    </tr>
                  </thead>
                  <tbody>
                    {runs.data!.items.map((run) => (
                      <React.Fragment key={run.id}>
                        <tr className="border-b last:border-0 hover:bg-muted/40">
                          <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                            {formatDateTime(run.created_at, user?.timezone)}
                          </td>
                          <td className="px-3 py-2">
                            <Badge variant={runVariant(run.status)}>{runLabel(run.status)}</Badge>
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">{run.trigger}</td>
                          <td className="px-3 py-2 text-muted-foreground">v{run.rule_version}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatNumber(run.candidates_seen)}</td>
                          <td className="px-3 py-2 text-right font-medium tabular-nums">{formatNumber(run.candidates_matched)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{formatNumber(run.pending_verification)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{formatNumber(run.candidates_rejected)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{formatNumber(run.domains_created)}</td>
                        </tr>
                        {run.error_message && (
                          <tr className="border-b last:border-0">
                            <td colSpan={9} className="px-3 pb-2 text-destructive" role="alert">
                              {run.error_message}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          {/* The versions the runs and matches above are stamped with. Without
              these, "v1" on a match refers to a rule body that no longer
              exists anywhere, and a change in hit count cannot be attributed. */}
          <TabsContent value="versions">
            {versions.isLoading ? (
              <LoadingState rows={3} />
            ) : (versions.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="没有版本记录" />
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">{versions.data!.notice}</p>
                {versions.data!.items.map((v) => (
                  <details key={v.version} className="rounded-md border p-3 text-xs">
                    <summary className="flex cursor-pointer flex-wrap items-center gap-2">
                      <Badge variant={v.current ? 'default' : 'muted'}>v{v.version}</Badge>
                      {v.current && <Badge variant="success">当前</Badge>}
                      <span className="truncate">{v.name}</span>
                      <span className="ml-auto text-muted-foreground">
                        {v.current ? '更新于 ' : '被替换于 '}
                        {formatDateTime(v.at, user?.timezone)}
                      </span>
                    </summary>
                    <pre className="mt-2 max-h-64 overflow-auto rounded bg-muted/40 p-2 text-[11px] leading-relaxed">
                      {JSON.stringify(v.rule_json, null, 2)}
                    </pre>
                  </details>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}

function statusVariant(status: string) {
  return ({ active: 'success', draft: 'muted', paused: 'warning', error: 'danger', archived: 'muted' } as const)[status] ?? 'muted'
}
function statusLabel(status: string): string {
  return { draft: '草稿', active: '运行中', paused: '已停用', error: '错误', archived: '已归档' }[status] ?? status
}
function runVariant(status: string) {
  return ({ succeeded: 'success', failed: 'danger', running: 'default', queued: 'muted', cancelled: 'muted', budget_exceeded: 'warning' } as const)[status] ?? 'muted'
}
function runLabel(status: string): string {
  return {
    queued: '排队中', running: '运行中', succeeded: '成功', failed: '失败',
    cancelled: '已取消', budget_exceeded: '达到平台扫描保护上限',
  }[status] ?? status
}
